<template>
  <div v-if="hasInvoicingEntitlement" class="batch-page no-gutters d-flex flex-column white">
    <div class="mt-1 white">
      <div class="px-4 py-4 d-flex align-center justify-space-between">
        <vs-heading v-if="!isMobileView" class="batch-header">Batches</vs-heading>
        <vs-button
          v-if="isMobileView"
          class="overlay-button"
          :icon="navStore.isNavVisible ? 'mdi-menu-open' : 'mdi-menu'"
          type="secondary"
          @click="navStore.toggleNav"
        />
      </div>
    </div>

    <v-divider class="mt-1"></v-divider>

    <job-filters-top-bar
      :entity-type="'Batch'"
      :enable-auto-complete="false"
      :enable-job-number-suggestion="false"
      :enable-job-filters-header="false"
      :is-fetching="false"
      :is-mobile-view="isMobileView"
      :data-updated-at="Date.now()"
      @refresh="() => {}"
    >
      <template #trailing>
        <batch-column-selector v-if="!isMobileView" :columns="allColumns" class="ml-2" />
        <batch-sort-dropdown class="ml-2" />
      </template>
    </job-filters-top-bar>
    <div class="batch-list">
      <div v-if="isMobileView" class="batch-table-sort-container px-4 py-3">
        <span class="batch-table-label">Batches</span>
        <batch-sort-dropdown />
      </div>
      <narrow-batch-table v-if="isMobileView" :batches="batches ?? []" />
      <wide-batch-table
        v-else
        :is-fetching="isLoading"
        :batches="batches ?? []"
        :itemsPerPage="getItemsPerPage"
      />
      <div class="flex-shrink-0 px-4 py-1">
        <filtered-jobs-list-pagination
          :current-page="getCurrentPage"
          :items-per-page="getItemsPerPage"
          :total-items="count"
          :is-fetching="isLoading"
          :is-count-fetching="false"
          :has-next-page="hasMore"
          :visible-count="batches.length"
          :update-page="updatePage"
          :update-items-per-page="updateItemsPerPage"
        />
      </div>
    </div>

    <right-drawer :full-width="isMobileView" :is-visible="isRightDrawerVisible">
      <router-view />
    </right-drawer>
  </div>
  <batch-marketing-page v-else />
</template>

<script lang="ts" setup>
import { useFilteredInvoicesQuery, useInvoiceCount } from '@/api/invoice'
import { computed } from 'vue'
import FilteredJobsListPagination from '@/components/jobs/job-search-page/FilteredJobsListPagination.vue'
import RightDrawer from '@/components/common/RightDrawer.vue'
import { useBatchSideSheetNavigation } from '@/components/jobs/navigation'
import NarrowBatchTable from '@/components/batches/NarrowBatchTable.vue'
import { useScreen } from '@/common/useScreen'
import VsButton from '@/components/vision/VsButton.vue'
import { useNavStore } from '@/components/nav/navStore'
import WideBatchTable from '@/components/batches/WideBatchTable.vue'
import { useFilteredBatchStore } from '@/components/batches/useFilteredBatchStore'
import BatchSortDropdown from '@/components/batches/BatchSortDropdown.vue'
import BatchColumnSelector from '@/components/batches/BatchColumnSelector.vue'
import { useOrderedBatchColumns } from '@/components/batches/useOrderedBatchColumn'
import { storeToRefs } from 'pinia'
import JobFiltersTopBar from '@/components/jobs/job-search-page/JobFiltersTopBar.vue'
import { useFeatureFlags } from '@/common/useFeatureFlags'
import { useFilteredStore } from '@/components/filters/useFilteredStore'
import { useStore } from 'vuex-composition-helpers'
import BatchMarketingPage from '@/components/batches/BatchMarketingPage.vue'

document.title = 'Batches - IronSight'

const store = useStore()
const hasInvoicingEntitlement = computed<boolean>(
  () => store.getters['permissions/getInvoicingEntitlement'],
)

const { isMobileView } = useScreen()
const navStore = useNavStore()

const { isRightDrawerVisible } = useBatchSideSheetNavigation('batch-search', 'batch-info-panel')

const batchStore = useFilteredBatchStore()
const { getCurrentPage, getItemsPerPage, sort } = storeToRefs(batchStore)
const { updatePage, updateItemsPerPage } = batchStore

const { getFilters } = useFilteredStore('Batch')

const { isLoading, data, refetch } = useFilteredInvoicesQuery(
  getFilters,
  sort,
  getCurrentPage,
  getItemsPerPage,
  { enabled: true },
)

const { count, countLoading, countRefetch } = useInvoiceCount(getFilters, { enabled: true })
const batches = computed(() => data.value?.data ?? [])
const hasMore = computed(() => data.value?.meta.hasNextPage ?? false)

const { allColumns } = useOrderedBatchColumns()
</script>

<style scoped>
.batch-list {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
}

.batch-page {
  height: 100%;
}

.batch-header {
  font-size: 22px !important;
}

.batch-table-label {
  font-size: 16px;
  font-weight: 600;
  color: #212121;
}

.batch-table-sort-container {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
